import { parseJsonPreservingIntegers } from '../../../lib/jsonValues.js';

export function readId(value) {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) throw new Error('IDENTITY');
  const id = String(value ?? '');
  if (!/^[1-9]\d*$/.test(id) || BigInt(id) > 9223372036854775807n) throw new Error('IDENTITY');
  return id;
}

// Admin agent/get takes a workflow ID; agent.id is a separate display identity.
export function recordTarget(kind, record) {
  if (kind !== 'agent' && kind !== 'workflow') throw new Error('IDENTITY');
  const id = kind === 'agent' ? record?.workFlowId ?? record?.workFlow?.id : record?.id;
  if (kind === 'agent' && record?.workFlowId != null && record?.workFlow?.id != null
    && readId(record.workFlowId) !== readId(record.workFlow.id)) throw new Error('IDENTITY');
  return { kind, id: readId(id) };
}

// IndexAppServiceImpl.getAgents/getWorkFlows expose workflow IDs as card id.
export function applicationTarget(kind, item) {
  if (kind !== 'agent' && kind !== 'workflow') throw new Error('IDENTITY');
  if (kind === 'agent' && (item?.workFlowId != null || item?.workFlow?.id != null)) return recordTarget(kind, item);
  return { kind, id: readId(item?.id ?? item?.appId) };
}

export function routeReadId(kind, query) {
  const explicit = query.get('workFlowId');
  // Agent display IDs cannot be resolved by the existing workflow-keyed GET.
  return readId(kind === 'agent' ? explicit : explicit ?? query.get('id'));
}

export function validateDetail(target, response) {
  const id = readId(target.id);
  if (response?.success !== true || !response.data || typeof response.data !== 'object') throw new Error('EMPTY');
  const value = response.data;
  if (target.kind === 'agent') {
    readId(value.id);
    if (readId(value.workFlowId) !== id || readId(value.workFlow?.id) !== id) throw new Error('IDENTITY');
  } else if (target.kind !== 'workflow' || readId(value.id) !== id) throw new Error('IDENTITY');
  return value;
}

export function createDetailReader(load, publish) {
  let generation = 0, controller;
  const cancel = () => { generation++; controller?.abort(); };
  return { cancel, async read(target) {
    cancel();
    const sequence = generation;
    controller = new AbortController();
    const signal = controller.signal;
    publish({ phase: 'loading', target, sequence, data: null, error: null });
    try {
      readId(target.id);
      const response = await load(target, { signal, silentError: true });
      if (sequence !== generation || signal.aborted) return;
      const data = validateDetail(target, response);
      publish({ phase: 'ready', target, sequence, data, error: null });
    } catch (error) {
      if (sequence !== generation || signal.aborted) return;
      publish({ phase: 'error', target, sequence, data: null, error: error.message });
    }
  } };
}

export const valueText = (value, absent) => value == null || value === '' ? absent : String(value);
export const flagValue = value => value === true || value === 'Y' || value === 1 ? 'yes'
  : value === false || value === 'N' || value === 0 ? 'no' : value == null || value === '' ? 'missing' : 'unknown';

export function visibility(raw) {
  if (raw == null) return 'unknown';
  try {
    const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(list) || list.some(key => !['agent', 'flow'].includes(key))) return 'invalid';
    return list.length ? [...new Set(list)].join(',') : 'none';
  } catch { return 'invalid'; }
}

export function lookupName(id, entries, language) {
  if (id == null || id === '') return { state: 'missing', id: null };
  const key = String(id);
  const found = Array.isArray(entries) && entries.find(item => String(item.id) === key);
  const title = found && (language === 'cn' ? found.titleCn : found.titleEn);
  return title ? { state: 'ready', id: key, title } : { state: 'unknown', id: key };
}

export function labelNames(raw, entries, language) {
  if (raw == null || raw === '') return { state: 'missing', items: [] };
  try {
    const ids = typeof raw === 'string' ? parseJsonPreservingIntegers(raw) : raw;
    if (!Array.isArray(ids) || ids.some(id => !['string', 'number'].includes(typeof id)
      || typeof id === 'number' && !Number.isSafeInteger(id))) throw new Error('INVALID');
    return { state: ids.length ? 'ready' : 'empty', items: ids.map(id => lookupName(id, entries, language)) };
  } catch { return { state: 'invalid', items: [] }; }
}

export function adaptDetail(kind, value) {
  return { kind, agentId: kind === 'agent' ? value.id : null,
    workflow: kind === 'agent' ? value.workFlow : value,
    usage: kind === 'agent' ? value.usage : undefined,
    configCn: kind === 'agent' ? value.configCn : null,
    configEn: kind === 'agent' ? value.configEn : null };
}

export const configText = value => value == null ? '' : typeof value === 'string' ? value : JSON.stringify(value);
export function safeCourseLink(value) {
  if (typeof value !== 'string') return null;
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; }
  catch { return null; }
}
